import { create } from 'zustand';

import { setCurrentAccountId } from '@/adapters/analytics/currentAccountId';
import { setCurrentUserId } from '@/adapters/analytics/currentUserId';
import type { AnalyticsAdapter } from '@/adapters/analytics/types';
import type { BffAdapter, BrowserAdapter, PkceAdapter } from '@/adapters/auth/types';
import { isTransientError, TRANSIENT_ERROR_BREADCRUMB } from '@/adapters/graphql/transientError';
import type { Logger } from '@/adapters/logger/types';
import type { SecureStoreAdapter } from '@/adapters/storage/types';
import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from '@/constants/analytics';
import { mapUnknownLoginError } from '@/stores/mapUnknownLoginError';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

interface AuthError {
  key: string;
  detail?: string;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AuthError | null;
}

interface AuthActions {
  clearError: () => void;
  loadStoredAuth: () => Promise<void>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

interface AuthAdapters {
  bff: BffAdapter;
  browser: BrowserAdapter;
  pkce: PkceAdapter;
  secureStore: SecureStoreAdapter;
  logger: Logger;
  analytics: AnalyticsAdapter;
  onBeforeLogout?: () => Promise<void>;
}

const initialState: AuthState = {
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

type OAuth2ErrorCode = 'access_denied' | 'server_error';

const OAUTH2_ERROR_KEYS: Record<OAuth2ErrorCode, string> = {
  access_denied: 'auth.errors.oauth.accessDenied',
  server_error: 'auth.errors.oauth.serverError',
};

const isOAuth2ErrorCode = (code: string): code is OAuth2ErrorCode => code in OAUTH2_ERROR_KEYS;

const parseOAuth2Error = (url: string): AuthError | null => {
  try {
    const parsed = new URL(url);
    const error = parsed.searchParams.get('error');

    if (!error) {
      return null;
    }

    if (!isOAuth2ErrorCode(error)) {
      return { key: 'auth.errors.oauth.unknown' };
    }

    return { key: OAUTH2_ERROR_KEYS[error] };
  } catch {
    return null;
  }
};

const parseCallbackUrl = (url: string): { code: string; state: string } | null => {
  try {
    const parsed = new URL(url);
    const code = parsed.searchParams.get('code');
    const state = parsed.searchParams.get('state');

    if (!code || !state) {
      return null;
    }

    return { code, state };
  } catch {
    return null;
  }
};

const createAuthStore = (adapters: AuthAdapters, redirectUri: string) =>
  create<AuthState & AuthActions>((set) => ({
    ...initialState,
    clearError: () => {
      set({ error: null });
    },
    loadStoredAuth: async () => {
      set({ isLoading: true });

      try {
        const token = await adapters.secureStore.getItem(TOKEN_KEY);
        const refreshToken = await adapters.secureStore.getItem(REFRESH_TOKEN_KEY);

        if (!token) {
          set({ isLoading: false });
          return;
        }

        adapters.analytics.track(ANALYTICS_EVENTS.AUTH_SESSION_RESTORED);
        set({ token, refreshToken, isAuthenticated: true, isLoading: false });
      } catch {
        set({ isLoading: false });
      }
    },
    login: async () => {
      adapters.logger.addBreadcrumb('Auth Session Started');
      adapters.analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_STARTED);
      set({ isLoading: true, error: null });

      try {
        const codeVerifier = adapters.pkce.generateCodeVerifier();
        const codeChallenge = await adapters.pkce.generateCodeChallenge(codeVerifier);

        const { redirectUrl, state } = await adapters.bff.getAuthorizationLink(
          codeChallenge,
          'S256',
        );

        const result = await adapters.browser.openAuthSession(redirectUrl, redirectUri);

        if (result.type !== 'success') {
          adapters.logger.addBreadcrumb('Auth Session Cancelled/Dismissed', {
            type: result.type,
          });
          adapters.analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_FAILED, {
            [ANALYTICS_PARAMS.REASON]: result.type,
          });
          set({ isLoading: false });
          return;
        }

        adapters.analytics.track(ANALYTICS_EVENTS.AUTH_OAUTH_CALLBACK_RECEIVED, {
          [ANALYTICS_PARAMS.OUTCOME]: 'success',
        });
        adapters.logger.addBreadcrumb('Browser Redirect Success');

        const oauthError = parseOAuth2Error(result.url);

        if (oauthError) {
          adapters.analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_FAILED, {
            [ANALYTICS_PARAMS.REASON]: 'oauth_error',
          });
          // Declining consent is a normal choice, not something to raise an
          // issue for; anything else from the provider is worth looking at.
          const oauthMessage = `OAuth Error: ${oauthError.key}`;

          if (oauthError.key === OAUTH2_ERROR_KEYS.access_denied) {
            adapters.logger.addBreadcrumb(oauthMessage);
          }

          if (oauthError.key !== OAUTH2_ERROR_KEYS.access_denied) {
            adapters.logger.captureMessage(oauthMessage, 'warning');
          }

          set({ isLoading: false, error: oauthError });
          return;
        }

        const params = parseCallbackUrl(result.url);

        if (!params) {
          adapters.analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_FAILED, {
            [ANALYTICS_PARAMS.REASON]: 'invalid_callback',
          });
          adapters.logger.captureMessage('Invalid Callback URL', 'error');
          set({
            isLoading: false,
            error: { key: 'auth.errors.invalidCallback' },
          });
          return;
        }

        if (params.state !== state) {
          adapters.analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_FAILED, {
            [ANALYTICS_PARAMS.REASON]: 'state_mismatch',
          });
          adapters.logger.captureMessage('Auth error: auth.errors.stateMismatch', 'error');
          set({
            isLoading: false,
            error: { key: 'auth.errors.stateMismatch' },
          });
          return;
        }

        adapters.logger.addBreadcrumb('Code Exchange Started');

        const tokenResponse = await adapters.bff.exchangeAuthorizationCode(
          params.state,
          params.code,
          codeVerifier,
        );

        await adapters.secureStore.setItem(TOKEN_KEY, tokenResponse.accessToken);
        await adapters.secureStore.setItem(REFRESH_TOKEN_KEY, tokenResponse.refreshToken);

        adapters.analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_SUCCESS);
        set({
          token: tokenResponse.accessToken,
          refreshToken: tokenResponse.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (error) {
        adapters.analytics.track(ANALYTICS_EVENTS.AUTH_LOGIN_FAILED, {
          [ANALYTICS_PARAMS.REASON]: 'bff_error',
        });

        const isTransient = isTransientError(error);

        if (isTransient) {
          adapters.logger.addBreadcrumb(TRANSIENT_ERROR_BREADCRUMB, { reason: 'bff_error' });
        }

        if (!isTransient) {
          adapters.logger.captureException(error);
        }

        set({
          isLoading: false,
          error: mapUnknownLoginError(error),
        });
      }
    },
    logout: async () => {
      adapters.logger.addBreadcrumb('Logout Started');
      try {
        await adapters.onBeforeLogout?.();
      } catch (e) {
        adapters.logger.captureException(e);
      }

      try {
        await adapters.bff.revokeAccessToken();
      } catch (e) {
        adapters.logger.captureException(e);
      }

      try {
        await adapters.secureStore.deleteItem(TOKEN_KEY);
        await adapters.secureStore.deleteItem(REFRESH_TOKEN_KEY);
      } catch (e) {
        adapters.logger.captureException(e);
      }

      adapters.logger.setUser(null);
      adapters.analytics.track(ANALYTICS_EVENTS.AUTH_LOGOUT_COMPLETED);
      setCurrentUserId(null);
      setCurrentAccountId(null);

      set({
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        error: null,
      });
    },
  }));

export { createAuthStore, initialState, parseCallbackUrl, parseOAuth2Error };
export type { AuthAdapters, AuthError };
